import type { FC, MouseEvent, ReactNode } from 'react'

import { Container } from '@/components/generals/container'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { PageHero } from '@/components/pages/page-hero'
import { Reveal } from '@/components/motion/reveal'

interface LegalSection {
    id: string
    label: string
}

interface LegalLayoutProps {
    title: string
    eyebrow: string
    sections: LegalSection[]
    note?: string
    children: ReactNode
}

export const LegalLayout: FC<LegalLayoutProps> = ({ title, eyebrow, sections, note, children }): ReactNode => {
    const { scrollTo } = useSmoothScroll()

    const jump = (event: MouseEvent<HTMLAnchorElement>, id: string): void => {
        event.preventDefault()
        scrollTo(`#${id}`, { offset: -110 })
        window.history.replaceState(null, '', `#${id}`)
    }

    return (
        <>
            <PageHero
                breadcrumbs={[
                    { label: 'Startseite', href: '/' },
                    { label: title }
                ]}
                eyebrow={eyebrow}
                title={title}
            />

            <section aria-label={title} className='pb-28 pt-16 lg:pb-40 lg:pt-24'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <aside className='lg:col-span-4'>
                        <Reveal className='lg:sticky lg:top-32'>
                            <p className='font-mono text-[0.68rem] uppercase tracking-[0.18em] text-ink/60'>
                                Inhalt
                            </p>
                            <ol className='mt-5 space-y-2 border-l border-ink/15 pl-5 text-[0.95rem]'>
                                {sections.map(section => (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            onClick={event => jump(event, section.id)}
                                            className='link-grow pb-0.5 text-ink/70 transition-colors hover:text-ink'
                                        >
                                            {section.label}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                            {note && (
                                <p className='mt-8 text-sm text-ink/55'>
                                    {note}
                                </p>
                            )}
                        </Reveal>
                    </aside>

                    <Reveal delay={0.1} className='lg:col-span-8'>
                        <article className='prose-legal max-w-3xl'>
                            {children}
                        </article>
                    </Reveal>
                </Container>
            </section>
        </>
    )
}
